import { useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  BookOpen, Briefcase, ChevronRight, CreditCard, Home, Lightbulb, LineChart, LogOut, Mail, MessageSquareText,
  Newspaper, Settings, ShieldCheck, Sparkles, User, Users, Wrench, X,
} from "lucide-react";
import { tierStyle } from "../lib/tiers";

/**
 * Navigation sur téléphone (sous 768 px) :
 * - MobileTabBar : barre fixe en bas de l'écran avec les 5 sections du site ;
 * - AccountSheet : panneau qui monte du bas (bouton compte de l'en-tête) : compte, offre, administration, déconnexion.
 * Sur ordinateur, rien ne change (menu en haut).
 */

// Couleur de chaque section quand elle est ouverte (celle de ses titres et boutons)
export const TABS = [
  { to: "/", label: "Accueil", icon: Home, end: true, on: "text-emerald-400", bar: "bg-emerald-400" },
  { to: "/articles", label: "Analyses", icon: Newspaper, on: "text-violet-400", bar: "bg-violet-400" },
  { to: "/portefeuilles-sideb", label: "Portefeuilles", icon: Briefcase, on: "text-emerald-400", bar: "bg-emerald-400" },
  { to: "/cours", label: "Cours", icon: BookOpen, on: "text-orange-400", bar: "bg-orange-400" },
  { to: "/simulateurs", label: "Outils", icon: Wrench, on: "text-orange-400", bar: "bg-orange-400" },
];

export function MobileTabBar() {
  return (
    <nav
      className="md:hidden fixed inset-x-0 bottom-0 z-50 border-t border-white/10 bg-[#0A0A0A]"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      aria-label="Navigation principale"
      data-testid="mobile-tabbar"
    >
      {/* Fond plein, sans flou : sur iPhone (Safari), le flou d'arrière-plan faisait disparaître
          les icônes et les mots de la barre pendant le défilement. */}
      <div className="grid grid-cols-5 h-16">
        {TABS.map(({ to, label, icon: Icon, end, on, bar }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            data-testid={`tab-${label.toLowerCase()}`}
            className={({ isActive }) =>
              `relative flex flex-col items-center justify-center gap-1 text-[10.5px] font-medium transition-colors ${
                isActive ? on : "text-zinc-500 active:text-zinc-300"
              }`
            }
          >
            {({ isActive }) => (
              <>
                {isActive && <span className={`absolute top-0 h-0.5 w-8 rounded-full ${bar}`} />}
                <Icon className="w-5 h-5" strokeWidth={isActive ? 2.2 : 1.8} />
                {label}
              </>
            )}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

const TIER_LABELS = { free: "Gratuit", essentiel: "Essentiel", pro: "Pro", max: "Max" };

const ADMIN_LINKS = [
  { to: "/admin/articles", label: "Analyses", icon: Newspaper },
  { to: "/admin/cours", label: "Cours", icon: BookOpen },
  { to: "/admin/portefeuilles-sideb", label: "Portefeuilles", icon: Briefcase },
  { to: "/admin/avis", label: "Avis", icon: MessageSquareText },
  { to: "/admin/idees", label: "Idées", icon: Lightbulb, badge: "ideas" },
  { to: "/admin/home-cards", label: "Cartes accueil", icon: Settings },
  { to: "/admin/newsletter", label: "Newsletter", icon: Mail },
  { to: "/admin/utilisateurs", label: "Utilisateurs", icon: Users },
  { to: "/admin/statistiques", label: "Statistiques", icon: LineChart },
  { to: "/admin/securite", label: "Sécurité 2FA", icon: ShieldCheck },
];

/** Rond avec l'initiale du membre, entouré de la couleur de son offre. */
export function Avatar({ user, size = "w-8 h-8" }) {
  const tier = user?.subscription_tier || "free";
  const initial = (user?.pseudo || user?.name || user?.email || "?").trim().charAt(0).toUpperCase();
  return (
    <span className={`${size} shrink-0 grid place-items-center rounded-full bg-white/[0.06] border text-sm font-semibold ${tierStyle(tier).badge}`}>
      {initial}
    </span>
  );
}

export function AccountSheet({ open, onClose, user, isAdmin, newIdeas = 0, onLogout }) {
  const { pathname } = useLocation();
  // Se ferme quand on change de page ; bloque le défilement de la page derrière
  useEffect(() => { if (open) onClose(); }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", onKey); };
  }, [open, onClose]);
  if (!open || !user) return null;
  const tier = user.subscription_tier || "free";
  const isPaid = tier !== "free";
  const row = "flex items-center gap-3 px-4 h-12 rounded-xl text-[15px] text-zinc-200 active:bg-white/5";
  return (
    <div className="md:hidden fixed inset-0 z-[70]" data-testid="account-sheet">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div
        className="absolute inset-x-0 bottom-0 max-h-[85vh] overflow-y-auto overscroll-contain rounded-t-2xl border-t border-white/10 bg-[#121214] px-3 pt-2 pb-6 animate-in slide-in-from-bottom duration-200"
        style={{ paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom))" }}
        role="dialog"
        aria-modal="true"
        aria-label="Mon compte"
      >
        <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-white/15" />
        <div className="flex items-center gap-3 px-3 pb-4 border-b border-white/5">
          <Avatar user={user} size="w-11 h-11" />
          <div className="min-w-0 flex-1">
            <div className="font-medium truncate">{user.pseudo || user.name || "Mon compte"}</div>
            <div className="text-xs text-zinc-500 truncate">{user.email}</div>
          </div>
          {isAdmin ? (
            <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.12em] border border-emerald-500/40 text-emerald-300">Admin</span>
          ) : (
            <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.12em] border ${tierStyle(tier).badge}`}>
              {TIER_LABELS[tier] || "Gratuit"}
            </span>
          )}
          <button type="button" onClick={onClose} className="p-2 -mr-1 text-zinc-500" aria-label="Fermer">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="py-2">
          <Link to="/account" className={row} data-testid="sheet-account">
            <User className="w-5 h-5 text-zinc-400" /> Mon compte <ChevronRight className="w-4 h-4 ml-auto text-zinc-600" />
          </Link>
          {!isAdmin && tier !== "max" && (
            <Link to="/upgrade" className={`${row} text-emerald-300`} data-testid="sheet-upgrade">
              <Sparkles className="w-5 h-5" /> {isPaid ? "Changer d'offre" : "Découvrir les offres"} <ChevronRight className="w-4 h-4 ml-auto text-zinc-600" />
            </Link>
          )}
          {isPaid && !isAdmin && (
            <Link to="/account" className={row}>
              <CreditCard className="w-5 h-5 text-zinc-400" /> Mon abonnement <ChevronRight className="w-4 h-4 ml-auto text-zinc-600" />
            </Link>
          )}
        </div>
        {isAdmin && (
          <div className="pt-3 pb-1 border-t border-white/5" data-testid="sheet-admin">
            <div className="px-4 pb-2 text-[10px] uppercase tracking-[0.2em] text-emerald-400">Administration</div>
            <div className="grid grid-cols-2 gap-2 px-1">
              {ADMIN_LINKS.map(({ to, label, icon: Icon, badge }) => (
                <Link key={to} to={to} className="relative flex items-center gap-2.5 h-12 px-3 rounded-xl border border-white/5 bg-white/[0.02] text-sm text-zinc-300 active:bg-white/5">
                  <Icon className="w-4 h-4 text-zinc-500 shrink-0" />
                  <span className="truncate">{label}</span>
                  {badge === "ideas" && newIdeas > 0 && (
                    <span className="ml-auto min-w-[1.25rem] h-5 px-1.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40 text-[10px] font-semibold grid place-items-center tabular-nums">
                      {newIdeas}
                    </span>
                  )}
                </Link>
              ))}
            </div>
          </div>
        )}
        <div className="pt-2 mt-2 border-t border-white/5">
          <button type="button" onClick={onLogout} className={`${row} w-full text-zinc-400`} data-testid="sheet-logout">
            <LogOut className="w-5 h-5" /> Se déconnecter
          </button>
        </div>
      </div>
    </div>
  );
}
